import React from 'react';
import CloseIcon from '@mui/icons-material//Close';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import { type IndividualQuestionBaseRepresentation, RequestedFile } from 'api';
import { MessageBox } from 'components';
import { ReportDate } from 'dashboard409a/services/types';
import { ResponsibleUser } from '../../InformationRequestModal/SourceResponsibleSelector/types';

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  flex: 1,
}));

const StyledTableHead = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  fontWeight: 'bold',
}));

const StyledTableRow = styled(TableRow)(() => ({
  border: 'none',
}));

const StyledTableCell = styled(TableCell)(() => ({
  border: 'none',
}));

const StyledChip = styled(Chip)(() => ({
  marginRight: '8px',
  marginBottom: '8px',
  display: 'inline-block',
}));

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  marginTop: theme.spacing(),
  marginBottom: theme.spacing(2.5),
}));

const ConfirmRequest = (props: {
  open: boolean;
  onClose: () => void;
  saving: boolean;
  hasSaved: boolean;
  reportDate: ReportDate | undefined;
  responsibleUser: ResponsibleUser | undefined;
  files: RequestedFile[] | undefined;
  questions: IndividualQuestionBaseRepresentation[] | undefined;
  notify: boolean;
  setNotify: (notify: boolean) => void;
  onSend: () => Promise<void>;
  onFinish: () => void;
}) => {
  const {
    files,
    hasSaved,
    notify,
    onClose,
    onFinish,
    onSend,
    open,
    questions,
    reportDate,
    responsibleUser,
    saving,
    setNotify,
  } = props;
  return (
    <Dialog open={open} onClose={onClose} title="Information Request" maxWidth="xl" fullWidth>
      {!saving && !hasSaved && (
        <>
          <DialogTitle id="customized-dialog-title">
            <Typography variant="h6">Information Request</Typography>
            <StyledCloseButton aria-label="close" onClick={onClose}>
              <CloseIcon />
            </StyledCloseButton>
          </DialogTitle>
          <DialogContent>
            <StyledTitle align="center" variant="h6">
              Do you confirm these details are correct?
            </StyledTitle>
            <br />
            <Table style={{ borderCollapse: 'collapse', border: 'none' }}>
              <TableBody>
                <StyledTableRow>
                  <StyledTableCell>
                    <StyledTableHead variant="body1">Measurement Date:</StyledTableHead>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1" color="primary">
                      {reportDate?.measurement_date?.date}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>
                    <StyledTableHead variant="body1">Responsible Party:</StyledTableHead>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1" color="primary">
                      {responsibleUser?.label}
                    </Typography>
                    {responsibleUser?.email && (
                      <Typography variant="body2" color="textSecondary">
                        {responsibleUser.email}
                      </Typography>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>
                    <StyledTableHead variant="body1">Requested Documents:</StyledTableHead>
                  </StyledTableCell>
                  <StyledTableCell>
                    {files?.map(file => (
                      <StyledChip key={file.id} size="small" label={file.name} color="primary" />
                    ))}
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>
                    <StyledTableHead variant="body1">Questions:</StyledTableHead>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1" color="primary">
                      {questions?.length}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>

                <StyledTableRow>
                  <StyledTableCell colSpan={2}>
                    <Box display="flex" justifyContent="start" alignItems="center">
                      <StyledTableHead variant="body1">Notify Responsible Party by Email</StyledTableHead>
                      <Checkbox checked={notify} color="primary" onChange={() => setNotify(!notify)} />
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={onClose}>
              <Typography color="primary">Back</Typography>
            </Button>
            <Button autoFocus variant="contained" color="secondary" onClick={onSend}>
              Confirm
            </Button>
          </DialogActions>
        </>
      )}
      {hasSaved && (
        <div>
          <StyledDialogContent>
            <MessageBox
              type="success"
              title="Request Successfully Updated"
              action={
                <Button variant="contained" color="primary" onClick={onFinish}>
                  Ok
                </Button>
              }
              secondaryAction={undefined}
            />
          </StyledDialogContent>
        </div>
      )}
    </Dialog>
  );
};

export default ConfirmRequest;
